/**
 * SamplePipelinePanel.jsx
 * ───────────────────────
 * Admin-only walkthrough seeder. Loads named scenarios (Mixed Pipeline,
 * Hot Cardiology, Stalled Deals, Implementation Queue) tagged
 * `_sample_data=true` server-side so they wipe cleanly without touching
 * real CRM data. Gated by the SAMPLE_DATA_ENABLED env var.
 *
 * Each scenario can be loaded standalone (default: replace any previously
 * loaded scenario) or appended alongside whatever is currently loaded
 * (toggle the "append" switch). Each scenario can also be removed
 * individually via its per-row remove button.
 *
 * Also exposes a "Reset Workspace" combo action: removes ALL sample leads
 * and clears in-progress dialer / quote-builder / learning-path state held
 * in session/localStorage. Auth token is preserved.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Sparkles, Plus, Trash2, RefreshCw, AlertTriangle, Check } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

/* ── Sample data keys (cleared on Reset Workspace) ───────────────── */
const SESSION_KEYS_TO_CLEAR = [
  "dialerActiveSession",
  "dialerQueuePosition",
  "dialer_admin_expanded_campaign",
  "dialer_expanded_campaign_id",
  "dialer_power_dial",
  "quote_context",
];
const LOCAL_KEYS_TO_CLEAR = [
  "dialer_campaign_presets",
  "lp_completed_chapters",
  "lp_completed_sections",
];

export default function SamplePipelinePanel({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyScenario, setBusyScenario] = useState(null);
  const [appendMode, setAppendMode] = useState(false);
  // Pending confirmation — `null` when no dialog is showing.
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/sample-data/status`, { headers });
      setStatus(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || "Failed to load status";
      toast.error(typeof d === "string" ? d : "Failed to load status");
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  useEffect(() => { refresh(); }, []);

  const seedScenario = async (scenarioId) => {
    setBusy(true);
    setBusyScenario(scenarioId);
    try {
      const r = await axios.post(
        `${API}/admin/sample-data/seed`,
        { scenario: scenarioId, replace: !appendMode },
        { headers },
      );
      toast.success(`${r.data?.scenario_name}: loaded ${r.data?.inserted ?? 0} leads${r.data?.replaced ? " (replaced prior set)" : ""}`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || "Seed failed";
      toast.error(typeof d === "string" ? d : "Seed failed");
    } finally {
      setBusy(false);
      setBusyScenario(null);
    }
  };

  const removeScenario = async (scenarioId, scenarioName) => {
    setBusy(true);
    setBusyScenario(scenarioId);
    try {
      const r = await axios.delete(
        `${API}/admin/sample-data/seed?scenario=${encodeURIComponent(scenarioId)}`,
        { headers },
      );
      toast.success(`Removed ${r.data?.removed ?? 0} ${scenarioName} leads`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || "Remove failed";
      toast.error(typeof d === "string" ? d : "Remove failed");
    } finally {
      setBusy(false);
      setBusyScenario(null);
      setPendingConfirm(null);
    }
  };

  const doRemoveAll = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/sample-data/seed`, { headers });
      toast.success(`Removed ${r.data?.removed ?? 0} sample leads`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || "Remove failed";
      toast.error(typeof d === "string" ? d : "Remove failed");
    } finally {
      setBusy(false);
      setPendingConfirm(null);
    }
  };

  const doResetWorkspace = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/sample-data/seed`, { headers });
      const removed = r.data?.removed ?? 0;
      try {
        SESSION_KEYS_TO_CLEAR.forEach((k) => sessionStorage.removeItem(k));
        LOCAL_KEYS_TO_CLEAR.forEach((k) => localStorage.removeItem(k));
      } catch { /* private-mode browsers may throw — non-fatal */ }
      toast.success(`Workspace reset — ${removed} sample leads + drafts cleared`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || "Reset failed";
      toast.error(typeof d === "string" ? d : "Reset failed");
    } finally {
      setBusy(false);
      setPendingConfirm(null);
    }
  };

  const askRemoveScenario = (scenario) => setPendingConfirm({
    kind: `remove-${scenario.id}`,
    title: `Remove ${scenario.name}?`,
    body: <>This will permanently remove the {scenario.loaded_count} <strong>{scenario.name}</strong> sample leads. Other loaded scenarios + your real pipeline are unaffected.</>,
    confirmLabel: "Remove scenario",
    confirmTone: "danger",
    onConfirm: () => removeScenario(scenario.id, scenario.name),
  });

  const askRemoveAll = () => setPendingConfirm({
    kind: "remove-all",
    title: "Remove all sample leads?",
    body: <>This will permanently remove every lead tagged <code style={{ background: "var(--code-bg, #f4f4f4)", padding: "1px 5px", borderRadius: 4 }}>_sample_data: true</code> across every scenario. Real leads are unaffected.</>,
    confirmLabel: "Remove all",
    confirmTone: "danger",
    onConfirm: doRemoveAll,
  });

  const askReset = () => setPendingConfirm({
    kind: "reset",
    title: "Reset workspace?",
    body: (
      <>
        <div style={{ marginBottom: 8 }}>This will:</div>
        <ul style={{ paddingLeft: 18, margin: 0, marginBottom: 10 }}>
          <li>Remove sample-pipeline leads across all scenarios</li>
          <li>Clear dialer queue position + active session</li>
          <li>Clear quote-builder draft context</li>
          <li>Reset learning-path progress markers</li>
        </ul>
        <div style={{ color: "var(--text-tertiary)" }}>Your sign-in session is preserved.</div>
      </>
    ),
    confirmLabel: "Reset workspace",
    confirmTone: "danger",
    onConfirm: doResetWorkspace,
  });

  if (loading) {
    return (
      <div data-testid="sample-pipeline-loading" style={{ padding: 28 }}>
        <div className="skeleton" style={{ height: 18, width: 220, marginBottom: 12 }} />
        <div className="skeleton" style={{ height: 12, width: 360, marginBottom: 6 }} />
        <div className="skeleton" style={{ height: 12, width: 300 }} />
      </div>
    );
  }

  const disabled = !status?.enabled;
  const scenarios = status?.scenarios || [];

  return (
    <div data-testid="sample-pipeline-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Sparkles style={{ width: 16, height: 16, color: "var(--purple)" }} />
        <h2 style={{ fontSize: 16, fontWeight: 650, margin: 0 }}>Sample Pipeline</h2>
        {status?.loaded && (
          <span data-testid="sample-pipeline-loaded-pill" style={{ marginLeft: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 999, background: "var(--green-light)", color: "var(--green)" }}>
            LOADED · {status.lead_count} leads
          </span>
        )}
      </div>
      <p style={{ fontSize: 12, color: "var(--text-tertiary)", lineHeight: 1.55, marginTop: 0, marginBottom: 18 }}>
        Pick a scenario below to spin up a tailored, anonymized lead set for sales walkthroughs or coaching. Each scenario is tagged{" "}
        <code style={{ background: "var(--code-bg, #f4f4f4)", padding: "1px 5px", borderRadius: 4, fontSize: 11 }}>_sample_data: true</code>{" "}
        so it cleans out instantly without touching your real pipeline.
      </p>

      {disabled && (
        <div data-testid="sample-pipeline-disabled" style={{ padding: "10px 14px", borderRadius: 8, background: "var(--amber-light)", border: "0.5px solid var(--amber)", marginBottom: 14, fontSize: 11, color: "var(--text-secondary)" }}>
          <AlertTriangle style={{ width: 11, height: 11, display: "inline-block", verticalAlign: "middle", marginRight: 6, color: "var(--amber)" }} />
          Sample data is disabled in this environment. Set <code>SAMPLE_DATA_ENABLED=true</code> in the backend to enable.
        </div>
      )}

      {/* ── Append-mode toggle ─────────────────────────────────────── */}
      <label
        data-testid="sample-pipeline-append-toggle"
        style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontSize: 11, color: "var(--text-secondary)", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1 }}
      >
        <input
          type="checkbox"
          checked={appendMode}
          onChange={(e) => setAppendMode(e.target.checked)}
          disabled={disabled || busy}
          style={{ cursor: disabled ? "default" : "pointer" }}
        />
        <span><strong>Append mode</strong> — load alongside other scenarios (default: replace)</span>
      </label>

      {/* ── Scenario grid ─────────────────────────────────────────── */}
      <div data-testid="sample-pipeline-scenarios" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginBottom: 18 }}>
        {scenarios.map((s) => (
          <div
            key={s.id}
            data-testid={`sample-scenario-${s.id}`}
            style={{
              padding: 14,
              borderRadius: 10,
              border: s.is_loaded ? "0.5px solid var(--green)" : "0.5px solid var(--border)",
              background: s.is_loaded ? "var(--green-light)" : "var(--bg)",
              opacity: disabled ? 0.6 : 1,
              transition: "border 0.15s, background 0.15s",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 650, margin: 0, color: "var(--text-primary)" }}>{s.name}</h3>
                  {s.is_loaded && (
                    <span data-testid={`sample-scenario-${s.id}-loaded`} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 7px", fontSize: 9, fontWeight: 700, borderRadius: 999, background: "var(--green)", color: "#fff" }}>
                      <Check style={{ width: 9, height: 9 }} /> LOADED · {s.loaded_count}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 11, color: "var(--text-tertiary)", margin: "4px 0 0", lineHeight: 1.5 }}>
                  {s.description}
                </p>
                <div style={{ fontSize: 10, color: "var(--text-quaternary, var(--text-tertiary))", marginTop: 6 }}>
                  {s.lead_count} leads in this scenario
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                <button
                  className="btn btn-primary"
                  data-testid={`sample-scenario-${s.id}-load-btn`}
                  onClick={() => seedScenario(s.id)}
                  disabled={disabled || busy}
                  style={{ fontSize: 11, padding: "5px 12px", opacity: (disabled || busy) ? 0.55 : 1, whiteSpace: "nowrap" }}
                >
                  {busyScenario === s.id ? "Loading…" : s.is_loaded ? "Reload" : "Load"}
                </button>
                {s.is_loaded && (
                  <button
                    className="btn"
                    data-testid={`sample-scenario-${s.id}-remove-btn`}
                    onClick={() => askRemoveScenario(s)}
                    disabled={busy}
                    style={{ fontSize: 11, padding: "5px 12px", opacity: busy ? 0.55 : 1, color: "var(--amber)" }}
                    title={`Remove just the ${s.name} leads`}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Footer actions ────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        {status?.loaded && (
          <button
            className="btn"
            data-testid="sample-pipeline-remove-all-btn"
            onClick={askRemoveAll}
            disabled={busy}
            style={{ fontSize: 12, padding: "8px 16px", opacity: busy ? 0.55 : 1 }}
          >
            <Trash2 style={{ width: 12, height: 12 }} /> Remove all sample leads
          </button>
        )}
        <button
          className="btn"
          data-testid="reset-workspace-btn"
          onClick={askReset}
          disabled={busy}
          title="Wipe sample leads across all scenarios + clear dialer queue position, draft quote context, and learning-path progress. Your sign-in is preserved."
          style={{
            fontSize: 12, padding: "8px 16px",
            opacity: busy ? 0.55 : 1,
            marginLeft: "auto",
            borderColor: "var(--amber)",
            color: "var(--amber)",
          }}
        >
          <RefreshCw style={{ width: 12, height: 12 }} /> Reset workspace
        </button>
      </div>

      <div style={{ borderTop: "0.5px solid var(--border)", paddingTop: 14, fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.6 }}>
        <div style={{ fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>How scenarios work</div>
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          <li><strong>Replace mode</strong> (default): loading a scenario wipes all other scenarios first — keeps the demo focused on a single story.</li>
          <li><strong>Append mode</strong>: stack multiple scenarios in the same pipeline view for richer walkthroughs (e.g. Hot Cardiology + Stalled Deals to demo prioritization).</li>
          <li>Sample leads are visible across Dashboard, Leads, Lead Focus, and Sales Dialer just like real leads.</li>
        </ul>
      </div>

      {pendingConfirm && (
        <ConfirmDialog
          title={pendingConfirm.title}
          body={pendingConfirm.body}
          confirmLabel={pendingConfirm.confirmLabel}
          confirmTone={pendingConfirm.confirmTone}
          busy={busy}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
          testid={`sample-pipeline-${pendingConfirm.kind}-confirm`}
        />
      )}
    </div>
  );
}
